import { useEffect, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { PendingEditsProvider, usePendingEdits } from "@/lib/pending-edits";
import { useApplyThemePrefs } from "@/stores/theme-prefs";

const CHROME_TRANSITION =
  "background-color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "border-color 1600ms cubic-bezier(0.22, 1, 0.36, 1)";

function MenuIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
      />
    </svg>
  );
}

/**
 * The page's scroll container, which reserves room for the SaveBar while it is up.
 *
 * <p>SaveBar is `fixed` and therefore out of flow: it floats OVER this element rather than pushing
 * it, so whatever sat at the bottom of a scrolled page (an "Add to company" button, the last row of
 * a list) ended up underneath the pill with no way to scroll it clear. Padding the scroll container
 * gives that content somewhere to go.
 *
 * <p>Only while a scope the bar saves is dirty (`dirty && !ownSaveAction`, the predicate SaveBar
 * filters with), read from the same registry the bar reads, so the two never disagree about whether
 * the bar is on screen. The value clears the bar's own geometry: it sits
 * `bottom-4` (1rem) with a `py-3` pill of about 3rem, so 5rem leaves a rem of air, and `sm:bottom-8`
 * needs one more.
 *
 * <p>Its own component because AppShellFrame renders the PROVIDER: a hook call in AppShellFrame's
 * body would run outside the context it is trying to read.
 */
function ScrollArea({ children }: { children: ReactNode }) {
  const ctx = usePendingEdits();
  const dirty = Object.values(ctx?.scopes ?? {}).some((scope) => scope.dirty && !scope.ownSaveAction);

  return (
    <main
      // `relative` ist hier kein Styling, sondern die Clipping-Zusicherung: ohne eigenen
      // containing block klammern sich absolut positionierte Nachkommen an den naechsten
      // positionierten Vorfahren OBERHALB dieses Containers und entkommen damit dem
      // overflow-auto. Ein einziges `sr-only` (Tailwind: position absolute) genuegt dann, um
      // das Dokument neben diesem Container ein zweites Mal scrollbar zu machen.
      className={`@container relative min-h-0 flex-1 overflow-auto${dirty ? " pb-20 sm:pb-24" : ""}`}
    >
      {children}
    </main>
  );
}

export function AppShellFrame({ sidebar, search, actions, contextBar, dock, overlays, children }: {
  sidebar: (idPrefix: string, onNavigate?: () => void) => ReactNode;
  search?: ReactNode; actions: ReactNode; contextBar?: ReactNode; dock?: ReactNode;
  overlays?: ReactNode; children: ReactNode;
}) {
  const intl = useIntl();
  useApplyThemePrefs();
  // Mobile navigation: under lg the sidebar is hidden and the hamburger
  // opens it as a drawer. Each drawer link closes via onNavigate; the
  // overlay (z-50) covers all other nav triggers, so closing on navigate
  // is enough. Escape also closes.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  return (
    <PendingEditsProvider>
      <div
        data-theme-mode="light"
        data-depth="off"
        className="flex h-screen bg-page text-ink"
        style={{ transition: CHROME_TRANSITION }}
      >
        {/* Persistent sidebar from lg+ - below that the drawer takes over. */}
        <aside className="hidden w-[200px] shrink-0 flex-col lg:flex">
          {sidebar("nav")}
        </aside>

        {/* Mobile drawer + backdrop. Stays mounted for the slide animation;
            without visibility it doesn't catch clicks (pointer-events-none). */}
        <div
          className={`fixed inset-0 z-50 lg:hidden ${mobileNavOpen ? "" : "pointer-events-none"}`}
          aria-hidden={!mobileNavOpen}
        >
          <div
            onClick={closeMobileNav}
            className={`absolute inset-0 bg-ink/30 transition-opacity duration-300 ${
              mobileNavOpen ? "opacity-100" : "opacity-0"
            }`}
          />
          <aside
            className={`absolute inset-y-0 left-0 flex w-[260px] max-w-[80%] flex-col bg-page shadow-xl transition-transform duration-300 ease-out ${
              mobileNavOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            {sidebar("nav-mobil", closeMobileNav)}
          </aside>
        </div>

        <div className="relative flex min-w-0 flex-1 flex-col">
          <header
            className="relative z-30 bg-page"
            style={{ transition: CHROME_TRANSITION }}
          >
            <div className="flex h-14 items-center gap-3 px-4 sm:gap-6 sm:px-6">
              <button
                id="nav-open-menu"
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label={intl.formatMessage({
                  id: "nav.openMenu",
                  defaultMessage: "Open menu",
                })}
                className="-ml-1 shrink-0 rounded-md p-2 text-ink transition hover:bg-ink/[0.06] hover:text-ink lg:hidden"
              >
                <MenuIcon />
              </button>
              <div className="flex min-w-0 flex-1 justify-center">
                {search}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {actions}
              </div>
            </div>
            {/* The acting-company statement, below the topbar and above the page: the mode is true
                of the session rather than of the route, so it belongs to the chrome. Renders
                nothing at all when the person is acting as themselves, which is almost always. */}
            {contextBar}
          </header>
          <ScrollArea>
            {children}
          </ScrollArea>
          {dock}
        </div>
        {overlays}
      </div>
    </PendingEditsProvider>
  );
}
